import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  analyzeDocument,
  computeScores,
  probeImage,
  type PageAudit,
  type Scores,
  type ImageInfo,
} from "./analysis";
import { siteData } from "@/store/siteData";
import { DEFAULT_LOGO } from "@/hooks/useLogoUrl";

// Denetlenecek sayfalar
const PAGES = [
  { key: "home", path: "/", label: "Ana Sayfa" },
  { key: "about", path: "/hakkimizda", label: "Hakkımızda" },
  { key: "projects", path: "/projelerimiz", label: "Projeler" },
  { key: "partners", path: "/stratejik-ortaklarimiz", label: "Ortaklar" },
];

// Bileşenlere gömülü (siteData'da olmayan) görseller — iframe çalışmasa da
// envantere girsin diye sabit liste.
const COMPONENT_IMAGES: { url: string; page: string; bg?: boolean }[] = [
  { url: "https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&w=1920&q=80", page: "Ana Sayfa" },
  { url: "https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=1920&q=80", page: "Ana Sayfa", bg: true },
  { url: "https://images.unsplash.com/photo-1531482615713-2afd69097998?auto=format&fit=crop&w=1920&q=80", page: "Projeler" },
  { url: "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=1400&q=80", page: "Ortaklar", bg: true },
  { url: "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=700&q=80", page: "Hakkımızda" },
];

// siteData + bileşen sabitlerinden görsel tohumları üret (iframe'den bağımsız kaynak)
function buildSeeds(): { url: string; page: string; bg: boolean }[] {
  const seeds: { url: string; page: string; bg: boolean }[] = [];
  const add = (url: string | undefined, page: string, bg = false) => {
    if (url && /^(https?:|\/)/.test(url)) seeds.push({ url, page, bg });
  };
  try {
    siteData.getFeatures().forEach((f) => add(f.imageUrl, "Ana Sayfa"));
    siteData.getTestimonials().forEach((t) => add(t.imageUrl, "Ana Sayfa"));
    const about = siteData.getAbout();
    add(about.heroImageUrl, "Hakkımızda");
    about.team.forEach((m) => add(m.imageUrl, "Hakkımızda"));
    siteData.getProjects().forEach((p) => add(p.image, "Projeler"));
    siteData.getPartners().forEach((p) => add(p.heroImage, "Ortaklar"));
    add(siteData.getLiveChat().agentImageUrl, "Genel");
    add(DEFAULT_LOGO, "Logo");
    add("/c2-logo-white.png", "Logo");
  } catch {
    /* siteData hazır değilse sadece bileşen sabitleriyle devam */
  }
  COMPONENT_IMAGES.forEach((c) => add(c.url, c.page, !!c.bg));
  return seeds;
}

// URL listesini ana pencerede sınırlı eşzamanlılıkla ölç
async function probeAll(urls: string[]): Promise<Map<string, { ok: boolean; w: number; h: number }>> {
  const map = new Map<string, { ok: boolean; w: number; h: number }>();
  const queue = [...urls];
  const worker = async () => {
    while (queue.length) {
      const url = queue.shift()!;
      map.set(url, await probeImage(url));
    }
  };
  await Promise.all(Array.from({ length: 6 }, () => worker()));
  return map;
}

type Tab = "overview" | "images" | "contrast" | "a11y" | "seo" | "type" | "responsive";

// ── Yardımcılar ───────────────────────────────────────────────────────────────

function scoreColor(n: number): string {
  if (n >= 90) return "#10B981";
  if (n >= 70) return "#F59E0B";
  if (n >= 50) return "#FB923C";
  return "#F43F5E";
}
function scoreLabel(n: number): string {
  if (n >= 90) return "Mükemmel";
  if (n >= 70) return "İyi";
  if (n >= 50) return "Geliştirilmeli";
  return "Kritik";
}
function fmtBytes(n: number): string {
  if (!n) return "—";
  if (n < 1024) return n + " B";
  if (n < 1024 * 1024) return (n / 1024).toFixed(1) + " KB";
  return (n / 1024 / 1024).toFixed(2) + " MB";
}

// iframe render'ının tamamlanmasını bekle
function waitForRender(iframe: HTMLIFrameElement): Promise<void> {
  return new Promise((resolve) => {
    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      resolve();
    };
    const start = Date.now();
    const poll = () => {
      if (settled) return;
      const doc = iframe.contentDocument;
      const ready =
        doc &&
        doc.readyState === "complete" &&
        doc.querySelector("footer, section, main");
      if (ready) {
        const imgs = Array.from(doc!.querySelectorAll("img")) as HTMLImageElement[];
        const pending = imgs.filter((i) => !i.complete).length;
        if (pending === 0 || Date.now() - start > 7000) {
          // içerik + görseller hazır → kısa yerleşim payı
          setTimeout(done, 600);
          return;
        }
      }
      if (Date.now() - start > 9000) {
        setTimeout(done, 300);
        return;
      }
      setTimeout(poll, 200);
    };
    iframe.addEventListener("load", () => setTimeout(poll, 250), { once: true });
    // load zaten geçmiş olabilir
    setTimeout(poll, 400);
  });
}

// Performans kaynak girdilerinden byte eşlemesi
function collectBytes(win: Window | null): Map<string, number> {
  const map = new Map<string, number>();
  if (!win) return map;
  try {
    const entries = win.performance.getEntriesByType("resource") as PerformanceResourceTiming[];
    for (const e of entries) {
      const bytes = e.transferSize || e.encodedBodySize || e.decodedBodySize || 0;
      if (bytes > 0) map.set(e.name, bytes);
    }
  } catch {
    /* yoksay */
  }
  return map;
}

// Bilinmeyen byte'lar için CORS fetch ile blob boyutu (önbellekten gelir)
async function probeBytes(urls: string[]): Promise<Map<string, number>> {
  const map = new Map<string, number>();
  const queue = [...urls];
  const worker = async () => {
    while (queue.length) {
      const url = queue.shift()!;
      try {
        const ctrl = new AbortController();
        const t = setTimeout(() => ctrl.abort(), 6000);
        const res = await fetch(url, { mode: "cors", signal: ctrl.signal, cache: "force-cache" });
        clearTimeout(t);
        const buf = await res.arrayBuffer();
        if (buf.byteLength > 0) map.set(url, buf.byteLength);
      } catch {
        /* erişilemedi → bilinmiyor */
      }
    }
  };
  await Promise.all([worker(), worker(), worker(), worker()]);
  return map;
}

// ── Skor kartı ────────────────────────────────────────────────────────────────

function ScoreRing({ value, label, icon }: { value: number; label: string; icon: string }) {
  const color = scoreColor(value);
  const circ = 2 * Math.PI * 34;
  const off = circ - (value / 100) * circ;
  return (
    <div className="bg-[#141E35] border border-[#2B3654] rounded-2xl p-5 flex flex-col items-center">
      <div className="relative w-[88px] h-[88px]">
        <svg width="88" height="88" className="-rotate-90">
          <circle cx="44" cy="44" r="34" fill="none" stroke="#1E2B4A" strokeWidth="8" />
          <circle
            cx="44" cy="44" r="34" fill="none" stroke={color} strokeWidth="8" strokeLinecap="round"
            strokeDasharray={circ} strokeDashoffset={off} style={{ transition: "stroke-dashoffset 1s ease" }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-black" style={{ color }}>{value}</span>
        </div>
      </div>
      <div className="flex items-center gap-1.5 mt-3">
        <i className={`${icon} text-sm`} style={{ color }}></i>
        <span className="text-white text-sm font-semibold">{label}</span>
      </div>
      <span className="text-xs mt-0.5" style={{ color }}>{scoreLabel(value)}</span>
    </div>
  );
}

// ── Ana sayfa ───────────────────────────────────────────────────────────────

export default function InspektorPage() {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const respIframeRef = useRef<HTMLIFrameElement>(null);
  const [phase, setPhase] = useState<"idle" | "running" | "done">("idle");
  const [progress, setProgress] = useState<{ i: number; label: string }>({ i: 0, label: "" });
  const [audits, setAudits] = useState<PageAudit[]>([]);
  const [scores, setScores] = useState<Scores | null>(null);
  const [tab, setTab] = useState<Tab>("overview");
  const [imgFilter, setImgFilter] = useState<"all" | "broken" | "noalt" | "oversized" | "bg">("all");
  const [scanDate, setScanDate] = useState<string>("");
  const [copied, setCopied] = useState(false);
  const [inventory, setInventory] = useState<ImageInfo[]>([]);

  const runAudit = useCallback(async () => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    setPhase("running");
    setAudits([]);
    setScores(null);
    setInventory([]);
    const results: PageAudit[] = [];

    for (let i = 0; i < PAGES.length; i++) {
      const p = PAGES[i];
      setProgress({ i: i + 1, label: p.label });
      iframe.src = p.path;
      await waitForRender(iframe);
      const doc = iframe.contentDocument;
      const win = iframe.contentWindow;
      if (!doc || !win) continue;
      let audit: PageAudit;
      try {
        audit = analyzeDocument(doc, win, p.label, p.path);
      } catch {
        continue;
      }
      // byte eşlemesi (performans girdileri)
      const bytes = collectBytes(win);
      for (const img of audit.images) {
        const b = bytes.get(img.src);
        if (b) img.bytes = b;
      }
      results.push(audit);
    }

    // ── Görsel envanteri: iframe DOM'u ∪ siteData tohumları (birleşik) ──
    const byUrl = new Map<string, ImageInfo>();
    const perfBytes = new Map<string, number>();
    for (const a of results) for (const img of a.images) {
      if (img.bytes) perfBytes.set(img.src, img.bytes);
      const ex = byUrl.get(img.src);
      if (!ex) byUrl.set(img.src, { ...img });
      else if (img.dispW > ex.dispW) { ex.dispW = img.dispW; ex.dispH = img.dispH; }
    }
    for (const s of buildSeeds()) {
      if (byUrl.has(s.url)) continue;
      let domain = "—";
      try { domain = new URL(s.url, location.href).hostname; } catch { /* yoksay */ }
      byUrl.set(s.url, {
        page: s.page, src: s.url, domain,
        alt: null, hasAltAttr: true, decorative: s.bg, naturalW: 0, naturalH: 0,
        dispW: 0, dispH: 0, broken: false, lazy: false, bytes: 0, oversized: false, oversizeFactor: 0, isBackground: s.bg,
      });
    }
    // Ana pencerede gerçek yükleme durumu + doğal boyut (yetkili kaynak)
    const probes = await probeAll(Array.from(byUrl.keys()));
    const dpr = window.devicePixelRatio || 1;
    for (const [url, info] of byUrl) {
      const p = probes.get(url);
      if (p) {
        info.broken = !p.ok;
        if (p.w) { info.naturalW = p.w; info.naturalH = p.h; }
      }
      info.bytes = perfBytes.get(url) || 0;
      if (info.dispW > 0 && info.naturalW > 0) {
        info.oversizeFactor = info.naturalW / (info.dispW * dpr);
        info.oversized = !info.broken && info.oversizeFactor > 2;
      }
    }
    const inv = Array.from(byUrl.values());
    // Bilinmeyen byte'ları CORS fetch ile tamamla
    const unknown = inv.filter((i) => !i.bytes && !i.broken).map((i) => i.src);
    if (unknown.length) {
      const probed = await probeBytes(unknown.slice(0, 70));
      for (const i of inv) if (!i.bytes) { const b = probed.get(i.src); if (b) i.bytes = b; }
    }

    setInventory(inv);
    setAudits(results);
    setScores(computeScores(results, inv));
    setScanDate(new Date().toLocaleString("tr-TR"));
    setPhase("done");
  }, []);

  useEffect(() => {
    const t = setTimeout(() => runAudit(), 600);
    return () => clearTimeout(t);
  }, [runAudit]);

  // ── Türetilmiş veriler ──
  const allImages = inventory;
  const realImages = allImages.filter((i) => !i.isBackground);
  const broken = realImages.filter((i) => i.broken);
  const noAlt = realImages.filter((i) => !i.hasAltAttr);
  const oversized = realImages.filter((i) => i.oversized);
  const totalBytes = allImages.reduce((s, i) => s + i.bytes, 0);
  const contrastFails = audits.flatMap((a) => a.contrast).filter((c) => c.level === "fail");
  const a11yIssues = audits.flatMap((a) => a.a11y);
  const a11yErrors = a11yIssues.filter((i) => i.severity === "error");

  const filteredImages =
    imgFilter === "all" ? allImages :
    imgFilter === "broken" ? allImages.filter((i) => i.broken) :
    imgFilter === "noalt" ? allImages.filter((i) => !i.hasAltAttr && !i.isBackground) :
    imgFilter === "oversized" ? allImages.filter((i) => i.oversized) :
    allImages.filter((i) => i.isBackground);

  const copyReport = () => {
    if (!scores) return;
    const lines: string[] = [];
    lines.push(`C2TEK — Görsel Denetim Raporu (${scanDate})`);
    lines.push(`Genel Skor: ${scores.overall}/100`);
    lines.push(`  Görseller: ${scores.images} · Erişilebilirlik: ${scores.a11y} · Kontrast: ${scores.contrast} · SEO: ${scores.seo}`);
    lines.push(`Görsel: ${realImages.length} (bozuk ${broken.length}, alt-yok ${noAlt.length}, büyük ${oversized.length}), toplam ~${fmtBytes(totalBytes)}`);
    lines.push(`Kontrast hatası: ${contrastFails.length} · A11y: ${a11yErrors.length} hata / ${a11yIssues.length - a11yErrors.length} uyarı`);
    if (broken.length) { lines.push("BOZUK GÖRSELLER:"); broken.forEach((b) => lines.push(`  - [${b.page}] ${b.src}`)); }
    navigator.clipboard?.writeText(lines.join("\n"));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const tabs: { id: Tab; label: string; icon: string; badge?: number }[] = [
    { id: "overview", label: "Genel Bakış", icon: "ri-dashboard-3-line" },
    { id: "images", label: "Görseller", icon: "ri-image-2-line", badge: broken.length },
    { id: "contrast", label: "Kontrast", icon: "ri-contrast-2-line", badge: contrastFails.length },
    { id: "a11y", label: "Erişilebilirlik", icon: "ri-wheelchair-line", badge: a11yErrors.length },
    { id: "seo", label: "SEO / Meta", icon: "ri-seo-line" },
    { id: "type", label: "Tipografi", icon: "ri-font-size-2" },
    { id: "responsive", label: "Responsive", icon: "ri-smartphone-line" },
  ];

  return (
    <div className="min-h-screen bg-[#0A1020] flex flex-col">
      {/* Üst bar */}
      <div className="sticky top-0 z-50 bg-[#0F1729]/95 backdrop-blur border-b border-[#1E2B4A] h-14 flex items-center px-5 gap-3">
        <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#3B82F6]/20 border border-[#3B82F6]/30">
          <i className="ri-radar-line text-[#3B82F6] text-sm"></i>
        </div>
        <div>
          <span className="text-white font-semibold text-sm">Görsel Denetim Merkezi</span>
          {scanDate && <span className="text-[#465886] text-xs ml-2">son tarama {scanDate}</span>}
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <button
            onClick={copyReport}
            disabled={phase !== "done"}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#1E2B4A] text-[#7B8FAF] hover:text-white rounded-lg text-xs cursor-pointer transition-colors border border-[#2B3654] disabled:opacity-40"
          >
            <i className={copied ? "ri-check-line text-emerald-400" : "ri-file-copy-line"}></i>
            {copied ? "Kopyalandı" : "Raporu Kopyala"}
          </button>
          <button
            onClick={runAudit}
            disabled={phase === "running"}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#3B82F6] text-white hover:bg-[#2563EB] rounded-lg text-xs cursor-pointer transition-colors disabled:opacity-50"
          >
            <i className={`ri-refresh-line ${phase === "running" ? "animate-spin" : ""}`}></i>
            Yeniden Tara
          </button>
          <Link to="/kontrol-paneli" className="flex items-center gap-1.5 px-3 py-1.5 bg-[#1E2B4A] text-[#7B8FAF] hover:text-white rounded-lg text-xs border border-[#2B3654]">
            <i className="ri-arrow-left-line"></i>Panel
          </Link>
        </div>
      </div>

      {/* Gizli analiz iframe'i */}
      <iframe
        ref={iframeRef}
        title="audit-frame"
        style={{ position: "fixed", left: -9999, top: 0, width: 1280, height: 900, border: 0, visibility: "hidden" }}
        aria-hidden="true"
      />

      {/* Tarama ekranı */}
      {phase === "running" && (
        <div className="flex-1 flex flex-col items-center justify-center gap-5 p-10">
          <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-[#3B82F6]/15 border border-[#3B82F6]/30">
            <i className="ri-radar-line text-[#3B82F6] text-3xl animate-pulse"></i>
          </div>
          <div className="text-center">
            <p className="text-white font-semibold">Site denetleniyor…</p>
            <p className="text-[#7B8FAF] text-sm mt-1">{progress.label} ({progress.i}/{PAGES.length}) sayfası gerçek tarayıcıda render edilip ölçülüyor</p>
          </div>
          <div className="w-72 h-1.5 bg-[#1E2B4A] rounded-full overflow-hidden">
            <div className="h-full bg-[#3B82F6] transition-all duration-500" style={{ width: `${(progress.i / PAGES.length) * 100}%` }}></div>
          </div>
        </div>
      )}

      {phase === "done" && scores && (
        <div className="flex-1 flex flex-col min-h-0">
          {/* Sekmeler */}
          <div className="bg-[#0F1729] border-b border-[#1E2B4A] px-3 flex items-center gap-1 overflow-x-auto">
            {tabs.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm whitespace-nowrap border-b-2 transition-colors cursor-pointer ${
                  tab === t.id ? "border-[#3B82F6] text-white" : "border-transparent text-[#7B8FAF] hover:text-white"
                }`}
              >
                <i className={t.icon}></i>
                {t.label}
                {t.badge ? (
                  <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-[#F43F5E]/20 text-[#F87171]">{t.badge}</span>
                ) : null}
              </button>
            ))}
          </div>

          <div className="flex-1 overflow-y-auto p-5">
            {tab === "overview" && <OverviewTab scores={scores} audits={audits} inventory={inventory} realImages={realImages} broken={broken} noAlt={noAlt} oversized={oversized} totalBytes={totalBytes} contrastFails={contrastFails.length} a11yIssues={a11yIssues} onJump={setTab} />}
            {tab === "images" && <ImagesTab images={filteredImages} filter={imgFilter} setFilter={setImgFilter} counts={{ all: allImages.length, broken: broken.length, noalt: noAlt.length, oversized: oversized.length, bg: allImages.filter(i=>i.isBackground).length }} />}
            {tab === "contrast" && <ContrastTab audits={audits} />}
            {tab === "a11y" && <A11yTab issues={a11yIssues} />}
            {tab === "seo" && <SeoTab audits={audits} />}
            {tab === "type" && <TypeTab audits={audits} />}
            {tab === "responsive" && <ResponsiveTab respRef={respIframeRef} audits={audits} />}
          </div>
        </div>
      )}

      {phase === "idle" && (
        <div className="flex-1 flex items-center justify-center">
          <p className="text-[#7B8FAF]">Hazırlanıyor…</p>
        </div>
      )}
    </div>
  );
}

// ── Genel Bakış ───────────────────────────────────────────────────────────────

function OverviewTab({
  scores, audits, inventory, realImages, broken, noAlt, oversized, totalBytes, contrastFails, a11yIssues, onJump,
}: {
  scores: Scores; audits: PageAudit[]; inventory: ImageInfo[]; realImages: ImageInfo[]; broken: ImageInfo[]; noAlt: ImageInfo[]; oversized: ImageInfo[];
  totalBytes: number; contrastFails: number; a11yIssues: PageAudit["a11y"]; onJump: (t: Tab) => void;
}) {
  const overflowPages = audits.filter((a) => a.overflowX);
  const issues: { icon: string; color: string; text: string; tab: Tab }[] = [];
  if (broken.length) issues.push({ icon: "ri-image-off-line", color: "#F43F5E", text: `${broken.length} bozuk görsel (yüklenemiyor)`, tab: "images" });
  if (noAlt.length) issues.push({ icon: "ri-text", color: "#F59E0B", text: `${noAlt.length} görselde alt metni yok`, tab: "images" });
  if (oversized.length) issues.push({ icon: "ri-expand-diagonal-line", color: "#FB923C", text: `${oversized.length} görsel gereğinden büyük yükleniyor`, tab: "images" });
  if (contrastFails) issues.push({ icon: "ri-contrast-2-line", color: "#F43F5E", text: `${contrastFails} metin/zemin kontrastı WCAG AA'yı geçemiyor`, tab: "contrast" });
  if (a11yIssues.filter(i=>i.severity==="error").length) issues.push({ icon: "ri-wheelchair-line", color: "#F43F5E", text: `${a11yIssues.filter(i=>i.severity==="error").length} erişilebilirlik hatası`, tab: "a11y" });
  if (overflowPages.length) issues.push({ icon: "ri-arrow-left-right-line", color: "#F59E0B", text: `${overflowPages.length} sayfada yatay taşma riski`, tab: "responsive" });

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Genel skor */}
      <div className="flex flex-col lg:flex-row gap-5 items-stretch">
        <div className="bg-gradient-to-br from-[#141E35] to-[#0F1729] border border-[#2B3654] rounded-2xl p-6 flex items-center gap-5 lg:w-80">
          <div className="relative w-24 h-24">
            <svg width="96" height="96" className="-rotate-90">
              <circle cx="48" cy="48" r="40" fill="none" stroke="#1E2B4A" strokeWidth="9" />
              <circle cx="48" cy="48" r="40" fill="none" stroke={scoreColor(scores.overall)} strokeWidth="9" strokeLinecap="round"
                strokeDasharray={2 * Math.PI * 40} strokeDashoffset={(2 * Math.PI * 40) * (1 - scores.overall / 100)} style={{ transition: "stroke-dashoffset 1s" }} />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-3xl font-black" style={{ color: scoreColor(scores.overall) }}>{scores.overall}</span>
            </div>
          </div>
          <div>
            <p className="text-[#7B8FAF] text-xs uppercase tracking-widest">Genel Skor</p>
            <p className="text-white text-xl font-bold">{scoreLabel(scores.overall)}</p>
            <p className="text-[#465886] text-xs mt-1">{audits.length} sayfa · {realImages.length} görsel tarandı</p>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 flex-1">
          <ScoreRing value={scores.images} label="Görseller" icon="ri-image-2-line" />
          <ScoreRing value={scores.a11y} label="Erişilebilirlik" icon="ri-wheelchair-line" />
          <ScoreRing value={scores.contrast} label="Kontrast" icon="ri-contrast-2-line" />
          <ScoreRing value={scores.seo} label="SEO / Meta" icon="ri-seo-line" />
        </div>
      </div>

      {/* Özet metrikler */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Toplam Görsel", value: realImages.length, sub: `${audits.length} sayfa`, color: "#60A5FA", icon: "ri-image-2-line" },
          { label: "Görsel Ağırlığı", value: fmtBytes(totalBytes), sub: "ölçülebilen", color: "#A78BFA", icon: "ri-hard-drive-2-line" },
          { label: "Bozuk Görsel", value: broken.length, sub: broken.length ? "acil" : "temiz", color: broken.length ? "#F43F5E" : "#10B981", icon: "ri-image-off-line" },
          { label: "Kontrast Hatası", value: contrastFails, sub: "WCAG AA", color: contrastFails ? "#F59E0B" : "#10B981", icon: "ri-contrast-2-line" },
        ].map((m, i) => (
          <div key={i} className="bg-[#141E35] border border-[#2B3654] rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <i className={`${m.icon} text-lg`} style={{ color: m.color }}></i>
              <span className="text-xs" style={{ color: m.color }}>{m.sub}</span>
            </div>
            <p className="text-white text-2xl font-black">{m.value}</p>
            <p className="text-[#7B8FAF] text-xs mt-0.5">{m.label}</p>
          </div>
        ))}
      </div>

      {/* Öncelikli sorunlar */}
      <div className="bg-[#0F1729] border border-[#2B3654] rounded-2xl p-5">
        <h3 className="text-white font-semibold mb-4 flex items-center gap-2"><i className="ri-error-warning-line text-[#F59E0B]"></i>Öncelikli Bulgular</h3>
        {issues.length === 0 ? (
          <div className="flex items-center gap-2 text-emerald-400 text-sm"><i className="ri-checkbox-circle-line text-lg"></i>Kritik bir sorun bulunamadı 🎉</div>
        ) : (
          <div className="space-y-2">
            {issues.map((it, i) => (
              <button key={i} onClick={() => onJump(it.tab)} className="w-full flex items-center gap-3 p-3 rounded-lg bg-[#141E35] border border-[#2B3654] hover:border-[#3B82F6]/50 text-left cursor-pointer transition-colors">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0" style={{ background: it.color + "20" }}>
                  <i className={it.icon} style={{ color: it.color }}></i>
                </div>
                <span className="text-[#D6DEEC] text-sm flex-1">{it.text}</span>
                <i className="ri-arrow-right-line text-[#465886]"></i>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Sayfa bazında */}
      <div className="bg-[#0F1729] border border-[#2B3654] rounded-2xl p-5">
        <h3 className="text-white font-semibold mb-4">Sayfa Bazında Özet</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[#7B8FAF] text-xs uppercase tracking-wider border-b border-[#2B3654]">
                <th className="text-left py-2 font-medium">Sayfa</th>
                <th className="text-right py-2 font-medium">Görsel</th>
                <th className="text-right py-2 font-medium">Bozuk</th>
                <th className="text-right py-2 font-medium">Kontrast</th>
                <th className="text-right py-2 font-medium">A11y</th>
                <th className="text-right py-2 font-medium">DOM</th>
                <th className="text-right py-2 font-medium">Taşma</th>
              </tr>
            </thead>
            <tbody>
              {audits.map((a) => {
                const imgs = inventory.filter((i) => i.page === a.page && !i.isBackground);
                const bk = imgs.filter((i) => i.broken).length;
                return (
                  <tr key={a.page} className="border-b border-[#1E2B4A] text-[#D6DEEC]">
                    <td className="py-2.5 font-medium text-white">{a.page}</td>
                    <td className="py-2.5 text-right">{imgs.length}</td>
                    <td className="py-2.5 text-right" style={{ color: bk ? "#F87171" : "#64748B" }}>{bk}</td>
                    <td className="py-2.5 text-right" style={{ color: a.contrast.length ? "#FBBF24" : "#64748B" }}>{a.contrast.length}</td>
                    <td className="py-2.5 text-right" style={{ color: a.a11y.length ? "#FBBF24" : "#64748B" }}>{a.a11y.length}</td>
                    <td className="py-2.5 text-right text-[#7B8FAF]">{a.domNodes}</td>
                    <td className="py-2.5 text-right">{a.overflowX ? <span className="text-[#F87171]">{a.scrollW}px</span> : <span className="text-emerald-400">✓</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── Görseller ─────────────────────────────────────────────────────────────────

function ImagesTab({
  images, filter, setFilter, counts,
}: {
  images: ImageInfo[];
  filter: string;
  setFilter: (f: "all" | "broken" | "noalt" | "oversized" | "bg") => void;
  counts: { all: number; broken: number; noalt: number; oversized: number; bg: number };
}) {
  const filters: { id: "all" | "broken" | "noalt" | "oversized" | "bg"; label: string; n: number; color: string }[] = [
    { id: "all", label: "Tümü", n: counts.all, color: "#60A5FA" },
    { id: "broken", label: "Bozuk", n: counts.broken, color: "#F43F5E" },
    { id: "noalt", label: "Alt Yok", n: counts.noalt, color: "#F59E0B" },
    { id: "oversized", label: "Büyük Boyut", n: counts.oversized, color: "#FB923C" },
    { id: "bg", label: "Arka Plan", n: counts.bg, color: "#A78BFA" },
  ];
  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {filters.map((f) => (
          <button key={f.id} onClick={() => setFilter(f.id)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium border cursor-pointer transition-colors ${filter === f.id ? "text-white" : "text-[#7B8FAF] border-[#2B3654] hover:text-white"}`}
            style={filter === f.id ? { background: f.color + "22", borderColor: f.color } : {}}
          >
            {f.label} <span className="opacity-70">({f.n})</span>
          </button>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {images.map((img, i) => (
          <div key={i} className="bg-[#141E35] border border-[#2B3654] rounded-xl overflow-hidden flex">
            <div className="w-28 h-28 flex-shrink-0 bg-[#0A1020] flex items-center justify-center relative">
              {img.broken ? (
                <i className="ri-image-off-line text-[#F43F5E] text-2xl"></i>
              ) : (
                <img src={img.src} alt="" className="w-full h-full object-cover" loading="lazy" />
              )}
              {img.isBackground && <span className="absolute top-1 left-1 text-[9px] px-1.5 py-0.5 rounded bg-[#A78BFA]/30 text-[#C4B5FD]">CSS</span>}
            </div>
            <div className="p-3 flex-1 min-w-0">
              <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#1E2B4A] text-[#7B8FAF]">{img.page}</span>
                {img.broken && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#F43F5E]/20 text-[#F87171]">BOZUK</span>}
                {!img.hasAltAttr && !img.isBackground && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#F59E0B]/20 text-[#FBBF24]">alt yok</span>}
                {img.oversized && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#FB923C]/20 text-[#FDBA74]">{img.oversizeFactor.toFixed(1)}× büyük</span>}
              </div>
              <p className="text-[#D6DEEC] text-xs truncate" title={img.src}>{img.src.split("/").pop()?.split("?")[0] || img.src}</p>
              <p className="text-[#465886] text-[11px] mt-0.5">{img.domain}</p>
              <div className="flex items-center gap-3 mt-1.5 text-[11px] text-[#7B8FAF]">
                {!img.isBackground && <span title="Doğal çözünürlük">{img.naturalW || "?"}×{img.naturalH || "?"}</span>}
                <span title="Ekrandaki boyut">→ {img.dispW}×{img.dispH}</span>
                <span className="ml-auto" style={{ color: img.bytes > 300 * 1024 ? "#FBBF24" : "#7B8FAF" }}>{fmtBytes(img.bytes)}</span>
              </div>
              {img.alt && <p className="text-[#5E7398] text-[11px] mt-1 truncate italic">“{img.alt}”</p>}
            </div>
          </div>
        ))}
        {images.length === 0 && <p className="text-[#7B8FAF] text-sm col-span-full text-center py-10">Bu filtrede görsel yok.</p>}
      </div>
    </div>
  );
}

// ── Kontrast ──────────────────────────────────────────────────────────────────

function ContrastTab({ audits }: { audits: PageAudit[] }) {
  const all = audits.flatMap((a) => a.contrast);
  return (
    <div className="max-w-5xl mx-auto">
      <div className="bg-[#0F1729] border border-[#2B3654] rounded-xl p-4 mb-4 text-sm text-[#A5B5CC] flex items-start gap-2">
        <i className="ri-information-line text-[#3B82F6] mt-0.5"></i>
        <span>WCAG 2.1 AA: normal metin için ≥ 4.5:1, büyük metin (≥24px veya ≥18.7px kalın) için ≥ 3:1 kontrast gerekir. Görsel/degrade üzerindeki metinler güvenli ölçülemediği için atlanır.</span>
      </div>
      {all.length === 0 ? (
        <div className="flex items-center gap-2 text-emerald-400 text-sm bg-[#141E35] border border-[#2B3654] rounded-xl p-5"><i className="ri-checkbox-circle-line text-lg"></i>Ölçülebilen tüm metinler AA eşiğini geçti.</div>
      ) : (
        <div className="space-y-2">
          {all.map((c, i) => (
            <div key={i} className="bg-[#141E35] border border-[#2B3654] rounded-xl p-3 flex items-center gap-4">
              <div className="px-4 py-3 rounded-lg flex-shrink-0 font-semibold" style={{ background: c.bg, color: c.fg }}>Örnek</div>
              <div className="flex-1 min-w-0">
                <p className="text-[#D6DEEC] text-sm truncate">“{c.sample}”</p>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#1E2B4A] text-[#7B8FAF]">{c.page}</span>
                  <span className="text-[11px] text-[#7B8FAF]">{c.fontPx}px {c.bold ? "kalın" : ""}</span>
                  <span className="text-[11px] text-[#7B8FAF]">{c.fg} / {c.bg}</span>
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-lg font-black" style={{ color: "#F43F5E" }}>{c.ratio}:1</p>
                <p className="text-[10px] text-[#7B8FAF]">gereken {c.required}:1</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Erişilebilirlik ─────────────────────────────────────────────────────────

function A11yTab({ issues }: { issues: PageAudit["a11y"] }) {
  const grouped = issues.reduce<Record<string, typeof issues>>((acc, it) => {
    (acc[it.page] ||= []).push(it);
    return acc;
  }, {});
  return (
    <div className="max-w-4xl mx-auto space-y-4">
      {issues.length === 0 && <div className="flex items-center gap-2 text-emerald-400 text-sm bg-[#141E35] border border-[#2B3654] rounded-xl p-5"><i className="ri-checkbox-circle-line text-lg"></i>Erişilebilirlik sorunu bulunamadı.</div>}
      {Object.entries(grouped).map(([page, list]) => (
        <div key={page} className="bg-[#0F1729] border border-[#2B3654] rounded-xl p-4">
          <p className="text-white font-semibold text-sm mb-3">{page}</p>
          <div className="space-y-2">
            {list.map((it, i) => (
              <div key={i} className="flex items-start gap-3 p-2.5 rounded-lg bg-[#141E35]">
                <div className="w-6 h-6 flex items-center justify-center rounded-md flex-shrink-0" style={{ background: it.severity === "error" ? "#F43F5E20" : "#F59E0B20" }}>
                  <i className={it.severity === "error" ? "ri-close-circle-line text-[#F87171]" : "ri-alert-line text-[#FBBF24]"}></i>
                </div>
                <div className="min-w-0">
                  <p className="text-[#D6DEEC] text-sm">{it.message}</p>
                  {it.detail && <p className="text-[#465886] text-xs mt-0.5 truncate">{it.detail}</p>}
                  <span className="text-[10px] text-[#5E7398]">{it.rule}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── SEO ───────────────────────────────────────────────────────────────────────

function SeoTab({ audits }: { audits: PageAudit[] }) {
  const Check = ({ ok, warn, children }: { ok: boolean; warn?: boolean; children: React.ReactNode }) => (
    <div className="flex items-start gap-2 text-sm">
      <i className={ok ? "ri-checkbox-circle-fill text-emerald-400 mt-0.5" : warn ? "ri-alert-fill text-[#FBBF24] mt-0.5" : "ri-close-circle-fill text-[#F87171] mt-0.5"}></i>
      <span className="text-[#A5B5CC]">{children}</span>
    </div>
  );
  return (
    <div className="max-w-4xl mx-auto space-y-4">
      {audits.map((a) => {
        const s = a.seo;
        return (
          <div key={a.page} className="bg-[#0F1729] border border-[#2B3654] rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-white font-semibold">{s.page}</p>
              <span className="text-[#465886] text-xs">{s.path}</span>
            </div>
            <div className="grid md:grid-cols-2 gap-x-6 gap-y-2">
              <Check ok={s.titleLen >= 10 && s.titleLen <= 65} warn={s.titleLen > 0}>
                Başlık {s.titleLen ? `(${s.titleLen} karakter)` : ""}: {s.title ? `“${s.title}”` : "— BOŞ"}
              </Check>
              <Check ok={s.descLen >= 50 && s.descLen <= 165} warn={s.descLen > 0}>
                Açıklama {s.descLen ? `(${s.descLen})` : ""}: {s.description ? s.description.slice(0, 50) + "…" : "— YOK"}
              </Check>
              <Check ok={s.h1Count === 1} warn={s.h1Count > 1}>H1 sayısı: {s.h1Count}</Check>
              <Check ok={!!s.lang && !s.lang.startsWith("en")} warn={!!s.lang}>Dil: {s.lang || "—"} {s.lang.startsWith("en") ? "(TR olmalı)" : ""}</Check>
              <Check ok={s.hasViewport}>Viewport meta</Check>
              <Check ok={s.favicon}>Favicon</Check>
              <Check ok={!!s.ogTitle} warn>OG Title {s.ogTitle ? "✓" : "— sosyal paylaşım için yok"}</Check>
              <Check ok={!!s.ogImage} warn>OG Image {s.ogImage ? "✓" : "— yok"}</Check>
              <Check ok={!!s.canonical} warn>Canonical {s.canonical ? "✓" : "— yok"}</Check>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Tipografi ─────────────────────────────────────────────────────────────────

function TypeTab({ audits }: { audits: PageAudit[] }) {
  const fonts = Array.from(new Set(audits.flatMap((a) => a.typography.fonts)));
  const sizes = Array.from(new Set(audits.flatMap((a) => a.typography.sizes))).sort((a, b) => b - a);
  const weights = Array.from(new Set(audits.flatMap((a) => a.typography.weights))).sort((a, b) => a - b);
  const home = audits[0];
  return (
    <div className="max-w-4xl mx-auto space-y-4">
      <div className="grid md:grid-cols-3 gap-3">
        <div className="bg-[#141E35] border border-[#2B3654] rounded-xl p-4">
          <p className="text-[#7B8FAF] text-xs uppercase tracking-widest mb-2">Yazı Tipleri ({fonts.length})</p>
          <div className="flex flex-wrap gap-1.5">{fonts.map((f) => <span key={f} className="text-xs px-2 py-1 rounded bg-[#1E2B4A] text-[#D6DEEC]">{f}</span>)}</div>
        </div>
        <div className="bg-[#141E35] border border-[#2B3654] rounded-xl p-4">
          <p className="text-[#7B8FAF] text-xs uppercase tracking-widest mb-2">Boyut Ölçeği ({sizes.length})</p>
          <div className="flex flex-wrap gap-1.5">{sizes.map((s) => <span key={s} className="text-xs px-2 py-1 rounded bg-[#1E2B4A] text-[#D6DEEC]">{s}px</span>)}</div>
          {sizes.length > 12 && <p className="text-[#FBBF24] text-xs mt-2"><i className="ri-alert-line"></i> {sizes.length} farklı boyut — ölçek sadeleştirilebilir</p>}
        </div>
        <div className="bg-[#141E35] border border-[#2B3654] rounded-xl p-4">
          <p className="text-[#7B8FAF] text-xs uppercase tracking-widest mb-2">Kalınlıklar</p>
          <div className="flex flex-wrap gap-1.5">{weights.map((w) => <span key={w} className="text-xs px-2 py-1 rounded bg-[#1E2B4A] text-[#D6DEEC]">{w}</span>)}</div>
        </div>
      </div>
      {home && (
        <div className="bg-[#0F1729] border border-[#2B3654] rounded-xl p-5">
          <p className="text-white font-semibold mb-3">Başlık Hiyerarşisi — {home.page}</p>
          <div className="space-y-1">
            {home.typography.headingOutline.map((h, i) => (
              <div key={i} className="flex items-center gap-3" style={{ paddingLeft: (parseInt(h.tag[1]) - 1) * 18 }}>
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${h.bad ? "bg-[#F43F5E]/20 text-[#F87171]" : "bg-[#3B82F6]/20 text-[#93C5FD]"}`}>{h.tag}</span>
                <span className="text-[#D6DEEC] text-sm truncate">{h.text}</span>
                {h.bad && <span className="text-[10px] text-[#F87171]">seviye atlandı</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Responsive ────────────────────────────────────────────────────────────────

function ResponsiveTab({ respRef, audits }: { respRef: React.RefObject<HTMLIFrameElement | null>; audits: PageAudit[] }) {
  const [w, setW] = useState(390);
  const [path, setPath] = useState("/");
  const [overflow, setOverflow] = useState<null | { scroll: number; client: number }>(null);
  const widths = [
    { w: 390, label: "Telefon", icon: "ri-smartphone-line" },
    { w: 768, label: "Tablet", icon: "ri-tablet-line" },
    { w: 1280, label: "Masaüstü", icon: "ri-computer-line" },
  ];

  const checkOverflow = () => {
    const f = respRef.current;
    if (!f?.contentDocument) return;
    const de = f.contentDocument.documentElement;
    setOverflow({ scroll: de.scrollWidth, client: de.clientWidth });
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {widths.map((b) => (
          <button key={b.w} onClick={() => { setW(b.w); setOverflow(null); }}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border cursor-pointer ${w === b.w ? "bg-[#3B82F6]/20 border-[#3B82F6] text-white" : "border-[#2B3654] text-[#7B8FAF] hover:text-white"}`}>
            <i className={b.icon}></i>{b.label} <span className="opacity-60">{b.w}px</span>
          </button>
        ))}
        <select value={path} onChange={(e) => { setPath(e.target.value); setOverflow(null); }}
          className="px-3 py-1.5 rounded-lg text-xs bg-[#141E35] border border-[#2B3654] text-[#D6DEEC] cursor-pointer ml-2">
          {PAGES.map((p) => <option key={p.path} value={p.path}>{p.label}</option>)}
        </select>
        <button onClick={checkOverflow} className="px-3 py-1.5 rounded-lg text-xs bg-[#1E2B4A] border border-[#2B3654] text-[#7B8FAF] hover:text-white cursor-pointer ml-auto">
          <i className="ri-ruler-line mr-1"></i>Taşma Ölç
        </button>
      </div>

      {overflow && (
        <div className={`mb-3 text-sm rounded-lg p-3 border ${overflow.scroll > overflow.client + 2 ? "bg-[#F43F5E]/10 border-[#F43F5E]/30 text-[#F87171]" : "bg-emerald-500/10 border-emerald-500/30 text-emerald-400"}`}>
          {overflow.scroll > overflow.client + 2
            ? <><i className="ri-error-warning-line"></i> Yatay taşma! İçerik {overflow.scroll}px, görünüm {overflow.client}px ({overflow.scroll - overflow.client}px taşıyor)</>
            : <><i className="ri-checkbox-circle-line"></i> Taşma yok — içerik {overflow.scroll}px ≤ görünüm {overflow.client}px</>}
        </div>
      )}

      <div className="flex justify-center bg-[#0A1020] border border-[#2B3654] rounded-xl p-4 overflow-auto">
        <div style={{ width: w, flexShrink: 0 }} className="bg-white rounded-lg overflow-hidden shadow-2xl">
          <iframe ref={respRef} src={path} title="responsive-preview" style={{ width: w, height: 720, border: 0, display: "block" }} />
        </div>
      </div>
      <p className="text-[#465886] text-xs text-center mt-3">Statik denetim sonuçlarına göre taşma riski olan sayfalar: {audits.filter(a=>a.overflowX).map(a=>a.page).join(", ") || "yok"}</p>
    </div>
  );
}
